/**
 * @file    descriptor.ts
 * @scope   SHARED
 * @summary Enterprise subtype descriptor: wires today's Enterprise code into the engine contract.
 * @consumes enterpriseReducer, isEnterpriseDataInvalid, EnterpriseV2MockDataBuilder
 * @produces ENTERPRISE_CAPABILITIES, ENTERPRISE_VIEW_KEYS, ENTERPRISE_DESCRIPTOR, EnterpriseDescriptor
 * @sideEffects None.
 * @notes   Phase 1: references existing code IN PLACE — nothing is moved. Views/State/Preview keep
 *          their folders because they follow the Enterprise shape (partitions → fields → calcs).
 *          `id` stays 'DynamicTable' for back-compat with the existing registry discriminator.
 */
import { EnterpriseGlobalModel } from 'models/Enterprise/EnterpriseGlobalModel';
import { ENTERPRISE_SERIALIZER_DELTA } from './serializerDelta';
import type { ChartCapabilities, ChartTypeDescriptor } from '../../engine/contracts';
import {
  ENTERPRISE_INITIAL_STATE,
  EnterpriseAction,
  enterpriseReducer,
} from './State/enterpriseReducer';
import { isEnterpriseDataInvalid } from './utils/validation/partitionValidation';
import {
  EnterpriseV2MockDataBuilder,
  EnterpriseV2MockResponse,
} from './Preview/EnterpriseV2MockDataBuilder';
import { EMPTY_SUFFIX_TYPES, ID_TYPES, RATING_TYPES } from './utils/filters/filterTypeResolver';

type MockTrend = Parameters<typeof EnterpriseV2MockDataBuilder.build>[1];

/** Calc types: 1 = Mean, 2 = Percentage, 3 = NPS, 4 = TotalCount. */
const ENTERPRISE_CALC_TYPES = [1, 2, 3, 4];

/**
 * Base capabilities = today's behaviour (everything on).
 * WHY: per-calc-type rules still live in `src/hooks/Enterprise/*`; they read these flags from
 * Phase 3 onwards, so declaring them now changes nothing.
 */
export const ENTERPRISE_CAPABILITIES: ChartCapabilities = {
  supportsPartitions: true,
  supportsPerFieldConfig: true,
  supportsPerFieldFilters: true,
  supportsProjectFrequency: true,
  allowNormalization: true,
  allowStatTesting: true,
  precisionEditable: true,
  allowedCalcTypes: ENTERPRISE_CALC_TYPES,
  allowedFilterTypes: [EMPTY_SUFFIX_TYPES, RATING_TYPES, ID_TYPES].flatMap((set) =>
    Array.from(set)
  ),
};

/** View keys, resolved to components by the ViewRegistry once the shell exists. */
export const ENTERPRISE_VIEW_KEYS = {
  /** Main-tab "build your data grid" accordion (host section). */
  dataGridSection: 'enterprise.dataGridSection',
  partitions: 'enterprise.partitions',
  fields: 'enterprise.fields',
  fieldFilters: 'enterprise.fieldFilters',
  partitionFilters: 'enterprise.partitionFilters',
  calculations: 'enterprise.calculations',
} as const;

export type EnterpriseDescriptor = ChartTypeDescriptor<
  'DynamicTable',
  EnterpriseGlobalModel,
  EnterpriseV2MockResponse,
  EnterpriseAction
>;

export const ENTERPRISE_DESCRIPTOR: EnterpriseDescriptor = {
  id: 'DynamicTable',
  optionValue: 'ManyCalculations',
  component: 'DynamicTable',
  // WHY: same alias the dashboard renders today; `DynamicTableOld` swap stays a host concern.
  renderBinding: { component: 'DynamicTable' },
  capabilities: ENTERPRISE_CAPABILITIES,
  views: [
    { key: ENTERPRISE_VIEW_KEYS.partitions, isEntry: true },
    { key: ENTERPRISE_VIEW_KEYS.fields },
    { key: ENTERPRISE_VIEW_KEYS.fieldFilters },
    { key: ENTERPRISE_VIEW_KEYS.partitionFilters },
    { key: ENTERPRISE_VIEW_KEYS.calculations },
  ],
  // WHY: mirrors today's `isDiyWidgetEngine` branches in MainView exactly.
  hostLayout: {
    showTrend: true,
    showLegacyOptions: false,
    showLegacyFilters: false,
    mainSection: ENTERPRISE_VIEW_KEYS.dataGridSection,
  },
  stateSlice: {
    // WHY: the reducer never mutates, so sharing the constant reference is safe.
    initialState: () => ENTERPRISE_INITIAL_STATE,
    reducer: enterpriseReducer,
  },
  validation: {
    isInvalid: (slice) => isEnterpriseDataInvalid(slice, undefined),
  },
  serializerDelta: ENTERPRISE_SERIALIZER_DELTA,
  preview: {
    previewMode: 'mock',
    mockBuilder: (slice, context) =>
      EnterpriseV2MockDataBuilder.build(slice, context.trend as MockTrend),
  },
};
